import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface GeneralSettings {
  id: string;
  name: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  themeColor: string;
  horizontalLogoUrl: string | null;
  verticalLogoUrl: string | null;
  faviconUrl: string | null;
  loginPageLogoUrl: string | null;
  address: string | null;
  tagline: string | null;
  mobile: string | null;
  heroDescription: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export type UpdateGeneralSettingsData = Partial<
  Pick<
    GeneralSettings,
    | 'name'
    | 'timezone'
    | 'dateFormat'
    | 'timeFormat'
    | 'themeColor'
    | 'horizontalLogoUrl'
    | 'verticalLogoUrl'
    | 'faviconUrl'
    | 'loginPageLogoUrl'
    | 'address'
    | 'tagline'
    | 'mobile'
    | 'heroDescription'
  >
>;

export type UpdateAppSettingsData = Partial<{
  appLink: string;
  iosAppLink: string;
  appVersion: string;
  iosAppVersion: string;
  forceAppUpdate: boolean;
  appMaintenance: boolean;
  teacherAppLink: string;
  teacherIosAppLink: string;
  teacherAppVersion: string;
  teacherIosAppVersion: string;
  teacherForceAppUpdate: boolean;
  teacherAppMaintenance: boolean;
}>;

export type AppSettingsView = GeneralSettings & UpdateAppSettingsData;

export type UpdateSubscriptionSettingsData = Partial<{
  additionalBillingDays: number;
  planExpiryWarningDays: number;
  defaultTrialDays: number;
  defaultStudentLimit: number;
  defaultStaffLimit: number;
}>;

export type SubscriptionSettingsView = GeneralSettings & UpdateSubscriptionSettingsData;

export interface GuidanceEntry {
  id: string;
  name: string;
  link: string;
  createdAt: string;
  updatedAt: string;
}

export interface LanguagePack {
  id: string;
  code: string;
  name: string;
  fileUrl: string | null;
  isRtl: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmailSettingsView {
  mailMailer: string;
  mailHost: string | null;
  mailPort: number;
  mailUsername: string | null;
  hasPassword: boolean;
  mailEncryption: string;
  mailSendFrom: string | null;
  emailVerified: boolean;
  updatedAt: string;
}

export interface UpdateEmailSettingsData {
  mailMailer?: string;
  mailHost?: string;
  mailPort?: number;
  mailUsername?: string;
  /** Omit to leave the currently-stored password unchanged. */
  mailPassword?: string;
  mailEncryption?: string;
  mailSendFrom?: string;
}

export interface TestEmailData extends UpdateEmailSettingsData {
  to: string;
}

export interface ContentPage {
  id: string;
  key: string;
  title: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadResult {
  url: string;
  name: string;
  size: number;
  mime: string;
}

export const PlatformSettingsApi = {
  getGeneral: async (): Promise<GeneralSettings> => unwrap(await api.get('/platform/settings/general')),

  updateGeneral: async (data: UpdateGeneralSettingsData): Promise<GeneralSettings> =>
    unwrap(await api.put('/platform/settings/general', data)),

  getApp: async (): Promise<AppSettingsView> => unwrap(await api.get('/platform/settings/app')),

  updateApp: async (data: UpdateAppSettingsData): Promise<AppSettingsView> =>
    unwrap(await api.put('/platform/settings/app', data)),

  getSubscription: async (): Promise<SubscriptionSettingsView> => unwrap(await api.get('/platform/settings/subscription')),

  updateSubscription: async (data: UpdateSubscriptionSettingsData): Promise<SubscriptionSettingsView> =>
    unwrap(await api.put('/platform/settings/subscription', data)),

  listGuidance: async (): Promise<GuidanceEntry[]> => unwrap(await api.get('/platform/settings/guidance')),

  createGuidance: async (data: { name: string; link: string }): Promise<GuidanceEntry> =>
    unwrap(await api.post('/platform/settings/guidance', data)),

  updateGuidance: async (id: string, data: { name?: string; link?: string }): Promise<GuidanceEntry> =>
    unwrap(await api.put(`/platform/settings/guidance/${id}`, data)),

  removeGuidance: async (id: string): Promise<{ deleted: true; id: string }> =>
    unwrap(await api.delete(`/platform/settings/guidance/${id}`)),

  listLanguages: async (): Promise<LanguagePack[]> => unwrap(await api.get('/platform/settings/languages')),

  createLanguage: async (data: { code: string; name: string; fileUrl?: string; isRtl?: boolean }): Promise<LanguagePack> =>
    unwrap(await api.post('/platform/settings/languages', data)),

  updateLanguage: async (id: string, data: { name?: string; fileUrl?: string; isRtl?: boolean }): Promise<LanguagePack> =>
    unwrap(await api.put(`/platform/settings/languages/${id}`, data)),

  removeLanguage: async (id: string): Promise<{ deleted: true; id: string }> =>
    unwrap(await api.delete(`/platform/settings/languages/${id}`)),

  uploadLanguageFile: async (file: File): Promise<UploadResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/platform/settings/uploads/language-file', fd));
  },

  getEmail: async (): Promise<EmailSettingsView> => unwrap(await api.get('/platform/settings/email')),

  updateEmail: async (data: UpdateEmailSettingsData): Promise<EmailSettingsView> =>
    unwrap(await api.put('/platform/settings/email', data)),

  testEmail: async (data: TestEmailData): Promise<{ sent: true }> =>
    unwrap(await api.post('/platform/settings/email/test-email', data)),

  listContentPages: async (): Promise<ContentPage[]> => unwrap(await api.get('/platform/settings/content-pages')),

  getContentPage: async (key: string): Promise<ContentPage> => unwrap(await api.get(`/platform/settings/content-pages/${key}`)),

  /** Unauthenticated — the same endpoint the "Public URL" link opens. */
  getPublicContentPage: async (key: string): Promise<ContentPage> => unwrap(await api.get(`/platform/settings/content-pages/${key}/public`)),

  updateContentPage: async (key: string, data: { title?: string; body?: string }): Promise<ContentPage> =>
    unwrap(await api.put(`/platform/settings/content-pages/${key}`, data)),

  upload: async (file: File): Promise<UploadResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/platform/settings/uploads', fd));
  },
};
